import { useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MobileNav } from "./MobileNav";
import { MegaMenu } from "./_components/MegaMenu";
import {
  FiMenu,
  FiChevronDown,
  FiUser,
  FiLogOut,
  FiHome,
  FiUsers,
  FiInbox,
  FiFile,
  FiMail,
  FiFileText,
  FiSettings,
  FiSend,
  FiStar,
  FiClipboard,
  FiZap,
  FiClock,
  FiDatabase,
  FiBarChart2,
  FiTrendingUp,
} from "@/lib/icons";
import { useAuth, useUser, useIsAuthenticated } from "@/features/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ColorblindToggle } from "@/components/common";

interface AdminNavItem {
  to: string;
  icon: typeof FiHome;
  label: string;
  end?: boolean;
}

interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

const adminNavGroups: AdminNavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/admin", icon: FiHome, label: "Dashboard", end: true },
      { to: "/admin/analytics", icon: FiBarChart2, label: "Analytics" },
    ],
  },
  {
    label: "CRM",
    items: [
      { to: "/admin/customers", icon: FiUsers, label: "Customers" },
      { to: "/admin/leads", icon: FiInbox, label: "Leads" },
      { to: "/admin/fact-finders", icon: FiFileText, label: "Fact Finders" },
      { to: "/admin/testimonials", icon: FiStar, label: "Testimonials" },
    ],
  },
  {
    label: "Marketing",
    items: [
      { to: "/admin/subscribers", icon: FiMail, label: "Subscribers" },
      { to: "/admin/campaigns", icon: FiSend, label: "Campaigns" },
      { to: "/admin/automations", icon: FiZap, label: "Automations" },
      { to: "/admin/scheduled-emails", icon: FiClock, label: "Scheduled" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/blog", icon: FiFileText, label: "Blog" },
      { to: "/admin/rba-data", icon: FiTrendingUp, label: "RBA Data" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/database", icon: FiDatabase, label: "Database" },
      // { to: "/admin/documents", icon: FiFile, label: "Documents" },
      // { to: "/admin/rates", icon: FiPercent, label: "Rates" },
    ],
  },
];

type ActiveMenu = "services" | "resources" | null;

export function Header() {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { logout } = useAuth();
  const user = useUser();
  const isAuthenticated = useIsAuthenticated();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleMouseEnter = (menu: ActiveMenu) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveMenu(menu);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  };

  const closeMenu = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveMenu(null);
  };

  return (
    <header
      className="sticky top-0 z-50 w-full bg-white border-b border-border/50"
      onMouseLeave={handleMouseLeave}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logo/PATH_FinanceSolutions_LogoLong_Normal.png"
              alt="Path Financial Solutions"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center">
            {/* Services (Home Loans + Business) */}
            <button
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${
                activeMenu === "services"
                  ? "text-primary"
                  : "text-foreground/80 hover:text-foreground"
              }`}
              onMouseEnter={() => handleMouseEnter("services")}
              aria-expanded={activeMenu === "services"}
            >
              Services
              <FiChevronDown
                className={`size-4 transition-transform ${
                  activeMenu === "services" ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Resources (Calculators + Guides) */}
            <button
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${
                activeMenu === "resources"
                  ? "text-primary"
                  : "text-foreground/80 hover:text-foreground"
              }`}
              onMouseEnter={() => handleMouseEnter("resources")}
              aria-expanded={activeMenu === "resources"}
            >
              Resources
              <FiChevronDown
                className={`size-4 transition-transform ${
                  activeMenu === "resources" ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Static Links */}
            <Link
              to="/about"
              className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              onMouseEnter={() => handleMouseEnter(null)}
            >
              About Us
            </Link>
            <Link
              to="/contact"
              className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              onMouseEnter={() => handleMouseEnter(null)}
            >
              Contact
            </Link>
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Accessibility Toggle */}
            <ColorblindToggle />

            {isAuthenticated && user && (
              <>
                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="hidden sm:inline-flex text-sm font-medium gap-2"
                    >
                      <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xs">
                        {user.name?.charAt(0).toUpperCase() || "U"}
                      </div>
                      {user.name?.split(" ")[0] || "Account"}
                      <FiChevronDown className="size-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />

                    {user.type === "admin" ? (
                      <>
                        {adminNavGroups.map((group, groupIndex) => (
                          <div key={group.label}>
                            {groupIndex > 0 && <DropdownMenuSeparator />}
                            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                              {group.label}
                            </DropdownMenuLabel>
                            {group.items.map((item) => (
                              <DropdownMenuItem key={item.to} asChild>
                                <NavLink
                                  to={item.to}
                                  end={item.end}
                                  className={({ isActive }) =>
                                    cn(
                                      "flex items-center gap-2 w-full cursor-pointer",
                                      isActive && "font-medium text-primary"
                                    )
                                  }
                                >
                                  <item.icon className="w-4 h-4" />
                                  {item.label}
                                </NavLink>
                              </DropdownMenuItem>
                            ))}
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                          Customer Portal
                        </DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to="/portal" className="flex items-center gap-2 cursor-pointer">
                            <FiHome className="w-4 h-4" />
                            Dashboard
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/portal/fact-finder" className="flex items-center gap-2 cursor-pointer">
                            <FiClipboard className="w-4 h-4" />
                            Fact Finder
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/portal/documents" className="flex items-center gap-2 cursor-pointer">
                            <FiFile className="w-4 h-4" />
                            Documents
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/portal/profile" className="flex items-center gap-2 cursor-pointer">
                            <FiUser className="w-4 h-4" />
                            Profile
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/portal/preferences" className="flex items-center gap-2 cursor-pointer">
                            <FiSettings className="w-4 h-4" />
                            Preferences
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}

                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
                    >
                      <FiLogOut className="w-4 h-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            {/* Hamburger Menu */}
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden p-2 -mr-2"
              aria-label="Open menu"
            >
              <FiMenu className="size-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mega Menus */}
      {activeMenu && (
        <div onMouseEnter={() => handleMouseEnter(activeMenu)}>
          <MegaMenu type={activeMenu} onClose={closeMenu} />
        </div>
      )}

      {/* Mobile Navigation */}
      <MobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
    </header>
  );
}
